import { BalloonEventModel, MetadataModel } from '../../models';
import { BalloonEventModelBuilder } from '../../models/BalloonEventModel';
import { UserModel } from '../../models/UserModel';
import UserRepository from '../UserRepository';
import BeatAPIBalloonEventTransformer from './BeatAPIBalloonEventTransformer';
import { ApiEndpoints as FullApiEndpoints } from '../../types';
import { apiFetch } from '../../utils/apiFetch';

export class ValidationError extends Error {
  constructor(message: string, public errors: any) {
    super(message);
    this.name = 'ValidationError';
    this.errors = errors;
    // Ensure the instance appears correctly as its subclassed type
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export interface BeatAPILoginPayload {
  email: string;
  password: string;
}

export interface BeatAPIRegisterPayload {
  email: string;
  password: string;
  name: string;
  language?: string;
}

interface ApiEndpoints extends Partial<FullApiEndpoints> {
  login: string;
  register: string;
  currentUser: string;
  users: string;
  bookmarks: string;
  forgotPassword?: string;
  resetPassword?: string;
}

export default class BeatAPIUserRepository extends UserRepository {
  private _apiEndpoints: ApiEndpoints;
  private _transformer: BeatAPIBalloonEventTransformer;
  private _partnerKey: string;

  constructor(
    metadata: MetadataModel,
    apiEndpoints: ApiEndpoints,
    partnerKey: string
  ) {
    super();
    this._transformer = new BeatAPIBalloonEventTransformer(
      new BalloonEventModelBuilder(),
      metadata
    );
    this._apiEndpoints = apiEndpoints;
    this._partnerKey = partnerKey;
  }

  protected async _loadBookmarks(): Promise<BalloonEventModel[]> {
    const response = await apiFetch(this._apiEndpoints.bookmarks, {
      method: 'GET',
      partnerKey: this._partnerKey,
      authToken: this.user!.token,
    });
    const data = await response.json();
    const events = data.data
      .map((e: any) => {
        try {
          return this._transformer.transform(e);
        } catch (e) {
          return null;
        }
      })
      .filter((e: any) => e);
    return events;
  }

  //POST _baseUrl/api/bookmarks/{id}/add with bearer token in header
  protected async _bookmark(id: string): Promise<void> {
    await apiFetch(this._apiEndpoints.bookmarks, {
      method: 'POST',
      partnerKey: this._partnerKey,
      authToken: this.user!.token,
      body: JSON.stringify({ balloon_id: id }),
    });
  }

  // POST _baseUrl/api/v2/users/me/bookmarks/bulk — used to silently merge
  // a guest's localStorage bookmarks into their account on login.
  public async bulkImportBookmarks(
    balloonIds: string[]
  ): Promise<{added: number; skipped: number} | null> {
    if (!balloonIds.length) return {added: 0, skipped: 0};
    const response = await apiFetch(`${this._apiEndpoints.bookmarks}/bulk`, {
      method: 'POST',
      partnerKey: this._partnerKey,
      authToken: this.user!.token,
      body: JSON.stringify({balloon_ids: balloonIds}),
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return {added: data.added ?? 0, skipped: data.skipped ?? 0};
  }

  //POST _baseUrl/api/bookmarks/{id}/remove with bearer token in header
  protected async _unbookmark(id: string): Promise<void> {
    await apiFetch(`${this._apiEndpoints.bookmarks}/${id}`, {
      method: 'DELETE',
      partnerKey: this._partnerKey,
      authToken: this.user!.token,
    });
  }

  //GET _baseUrl/api/current with bearer token in header
  protected async _loadUser(): Promise<UserModel> {
    const response = await apiFetch(this._apiEndpoints.currentUser, {
      method: 'GET',
      partnerKey: this._partnerKey,
      authToken: this.user!.token,
    });
    const data = await response.json();
    const events = data.data.bookmarkedBalloons
      .map((e: any) => {
        try {
          return this._transformer.transform(e);
        } catch (e) {
          return null;
        }
      })
      .filter((e: any) => e);
    return new UserModel(
      data.data.id,
      this.user!.token,
      data.data.email,
      data.data.name,
      data.data.permissions,
      events,
      data.data.avatar,
      data.data.hasPassword || false
    );
  }

  // POST _baseUrl/api/login {email, password}
  protected async _login(payload: BeatAPILoginPayload): Promise<UserModel> {
    const response = await apiFetch(this._apiEndpoints.login, {
      method: 'POST',
      body: JSON.stringify(payload),
      partnerKey: this._partnerKey,
    });
    const data = await response.json();
    if (response.status !== 200) {
      if (data.message === 'Validation errors occurred' && data.errors) {
        // Throw a custom error with validation details
        throw new ValidationError('Validation errors occurred', data.errors);
      } else {
        // Throw a general error if it's not a validation issue
        throw new Error(
          data.message || 'An error occurred during registration.'
        );
      }
    }
    return new UserModel(null, data.token, null, null, data.permissions, []);
  }

  // POST _baseUrl/api/register {email, password, name}
  // Returns void - user must verify email before logging in
  protected async _register(
    payload: BeatAPIRegisterPayload
  ): Promise<void> {
    const response = await apiFetch(this._apiEndpoints.register, {
      method: 'POST',
      body: JSON.stringify(payload),
      partnerKey: this._partnerKey,
    });
    const data = await response.json();

    if (response.status !== 201) {
      if (data.message === 'Validation errors occurred' && data.errors) {
        // Throw a custom error with validation details
        throw new ValidationError('Validation errors occurred', data.errors);
      } else {
        // Throw a general error if it's not a validation issue
        throw new Error(data.message || 'An error occurred during registration.');
      }
    }
    // Registration successful - user needs to verify email
    // No token returned, no user logged in
  }

  protected async _logout(): Promise<void> {}

  protected async _deleteUser(): Promise<void> {
    const response = await apiFetch(
      `${this._apiEndpoints.users}/${this.user!.id}`,
      {
        method: 'DELETE',
        partnerKey: this._partnerKey,
        authToken: this.user!.token,
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || data.message || 'Failed to delete user');
    }

    // Backend anonymizes the user but doesn't delete
    // The user data is returned but we treat this as a logout scenario
  }

  protected async _changePassword(
    currentPassword: string | null,
    newPassword: string
  ): Promise<void> {
    const payload: Record<string, string> = {
      new_password: newPassword,
    };

    if (currentPassword) {
      payload.current_password = currentPassword;
    }

    const response = await apiFetch(
      `${this._apiEndpoints.users}/${this.user!.id}/password`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
        partnerKey: this._partnerKey,
        authToken: this.user!.token,
      }
    );

    if (!response.ok) {
      const data = await response.json();
      // Laravel returns validation errors with an "errors" object
      if (data.errors && typeof data.errors === 'object') {
        throw new ValidationError(
          data.message || 'Validation errors occurred',
          data.errors
        );
      }
      throw new Error(data.message || 'Failed to change password');
    }
  }

  // Legacy OAuth methods - DEPRECATED, will be removed in 6 months
  public async googleLogin(token: string): Promise<UserModel> {
    const response = await apiFetch(this._apiEndpoints.googleLogin!, {
      method: 'POST',
      body: JSON.stringify({ data: { idToken: token } }),
      partnerKey: this._partnerKey,
    });

    // Check content type before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('[googleLogin] Non-JSON response:', response.status, text.substring(0, 200));
      throw new Error(`Google login failed: Server returned ${response.status} (expected JSON, got ${contentType || 'unknown'})`);
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Google login failed');
    }

    this.user = new UserModel(
      null,
      data.token,
      null,
      null,
      data.permissions,
      []
    );
    return this.user;
  }

  public async discordLogin(token: string): Promise<UserModel> {
    const response = await apiFetch(this._apiEndpoints.discordLogin!, {
      method: 'POST',
      body: JSON.stringify({ token }),
      partnerKey: this._partnerKey,
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Discord login failed');
    }

    this.user = new UserModel(
      null,
      data.token,
      null,
      null,
      data.permissions,
      []
    );
    return this.user;
  }

  /**
   * Open OAuth redirect URL in popup window
   * @param provider OAuth provider (google, discord, etc.)
   * @returns Popup window reference
   */
  public openOAuthPopup(provider: string): Window | null {
    const redirectUrl = this._apiEndpoints.oauthRedirect!.replace(
      '{provider}',
      provider
    );
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    return window.open(
      redirectUrl,
      'oauth-popup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  }

  /**
   * Handle OAuth callback from popup
   * Uses BroadcastChannel for cross-window communication (works despite COOP headers)
   * Falls back to postMessage for legacy backend support
   * @param provider OAuth provider
   * @returns Promise that resolves with UserModel when OAuth completes
   */
  public async handleOAuthCallback(provider: string): Promise<UserModel> {
    return new Promise((resolve, reject) => {
      const popup = this.openOAuthPopup(provider);

      if (!popup) {
        reject(new Error('Failed to open OAuth popup'));
        return;
      }

      let isResolved = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let channel: BroadcastChannel | null = null;

      // Cleanup function
      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (channel) {
          channel.close();
          channel = null;
        }
        window.removeEventListener('message', postMessageHandler);
      };

      // Handle successful OAuth data
      const handleOAuthSuccess = async (userData: {
        id: string;
        email: string;
        name: string;
        avatar?: string;
      }, token: string) => {
        if (isResolved) return;
        isResolved = true;
        cleanup();

        try {
          this.user = new UserModel(
            userData.id,
            token,
            userData.email,
            userData.name,
            [],
            [],
            userData.avatar
          );

          this.user = await this._loadUser();
          resolve(this.user!);
        } catch (error) {
          reject(error);
        }
      };

      // Handle OAuth error
      const handleOAuthError = (error: string) => {
        if (isResolved) return;
        isResolved = true;
        cleanup();
        reject(new Error(error || 'OAuth authentication failed'));
      };

      // BroadcastChannel listener (primary - works despite COOP headers)
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel('oauth-channel');
        channel.onmessage = (event) => {
          if (event.data.provider && event.data.provider !== provider) {
            return;
          }

          if (event.data.type === 'oauth-success') {
            handleOAuthSuccess(event.data.user, event.data.token);
          } else if (event.data.type === 'oauth-error') {
            handleOAuthError(event.data.error);
          }
        };
      }

      // PostMessage listener (fallback for legacy backend)
      const postMessageHandler = async (event: MessageEvent) => {
        const apiUrl = new URL(
          this._apiEndpoints.oauthRedirect!.replace('{provider}', provider)
        );
        if (event.origin !== apiUrl.origin) {
          return;
        }

        if (event.data.type === 'oauth-success') {
          handleOAuthSuccess(event.data.user, event.data.token);
        } else if (event.data.type === 'oauth-error') {
          handleOAuthError(event.data.error);
        }
      };

      window.addEventListener('message', postMessageHandler);

      // Timeout after 5 minutes (user may take time to authenticate)
      timeoutId = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          reject(new Error('OAuth authentication timed out'));
        }
      }, 5 * 60 * 1000);
    });
  }

  /**
   * Request a password reset email
   * @param email User's email address
   */
  public async forgotPassword(email: string): Promise<void> {
    if (!this._apiEndpoints.forgotPassword) {
      throw new Error('Forgot password endpoint not configured');
    }

    const response = await apiFetch(this._apiEndpoints.forgotPassword, {
      method: 'POST',
      body: JSON.stringify({ email }),
      partnerKey: this._partnerKey,
    });

    if (!response.ok) {
      const data = await response.json();
      if (data.errors && typeof data.errors === 'object') {
        throw new ValidationError(
          data.message || 'Validation errors occurred',
          data.errors
        );
      }
      throw new Error(data.message || 'Failed to send password reset email');
    }
  }

  /**
   * Reset password using the token from email
   * @param token Password reset token
   * @param email User's email address
   * @param password New password
   * @param passwordConfirmation Password confirmation
   */
  public async resetPassword(
    token: string,
    email: string,
    password: string,
    passwordConfirmation: string
  ): Promise<void> {
    if (!this._apiEndpoints.resetPassword) {
      throw new Error('Reset password endpoint not configured');
    }

    const response = await apiFetch(this._apiEndpoints.resetPassword, {
      method: 'POST',
      body: JSON.stringify({
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      }),
      partnerKey: this._partnerKey,
    });

    if (!response.ok) {
      const data = await response.json();
      if (data.errors && typeof data.errors === 'object') {
        throw new ValidationError(
          data.message || 'Validation errors occurred',
          data.errors
        );
      }
      throw new Error(
        data.message || 'This password reset link is invalid or has expired.'
      );
    }
  }

  protected async _uploadAvatar(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await apiFetch(
      `${this._apiEndpoints.users}/${this.user!.id}/avatar`,
      {
        method: 'POST',
        body: formData,
        partnerKey: this._partnerKey,
        authToken: this.user!.token,
      }
    );

    if (!response.ok) {
      const data = await response.json();
      // Laravel returns validation errors with an "errors" object
      if (data.errors && typeof data.errors === 'object') {
        throw new ValidationError(
          data.message || 'Validation errors occurred',
          data.errors
        );
      }
      throw new Error(data.error || data.message || 'Failed to upload avatar');
    }
  }

  protected async _deleteAvatar(): Promise<void> {
    const response = await apiFetch(
      `${this._apiEndpoints.users}/${this.user!.id}/avatar`,
      {
        method: 'DELETE',
        partnerKey: this._partnerKey,
        authToken: this.user!.token,
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || data.message || 'Failed to delete avatar');
    }
  }
}
